import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { Button } from '@/components/ui/button';
import { Copy, Download } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import type { Artifact } from '@/types';
import 'highlight.js/styles/github.css';

interface MarkdownRendererProps {
  artifact: Artifact;
}

export function MarkdownRenderer({ artifact }: MarkdownRendererProps) {
  const markdownContent = typeof artifact.data === 'string' 
    ? artifact.data 
    : String(artifact.data);

  const handleCopy = () => {
    navigator.clipboard.writeText(markdownContent);
  };

  const handleDownload = () => {
    const blob = new Blob([markdownContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = artifact.name.endsWith('.md') ? artifact.name : `${artifact.name}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!markdownContent.trim()) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          The markdown content is empty.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopy}
        >
          <Copy className="h-4 w-4" />
          Copy
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownload}
        >
          <Download className="h-4 w-4" />
          Download
        </Button>
      </div>

      {/* Markdown Content */}
      <div className="border rounded-lg p-6 bg-background max-h-96 overflow-auto">
        <div className="prose prose-sm max-w-none dark:prose-invert">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight]}
            components={{
              // Custom styling for code blocks
              code: ({ className, children, ...props }) => {
                const isInline = !className?.includes('language-');
                return isInline ? (
                  <code
                    className="px-1.5 py-0.5 rounded bg-muted text-sm font-mono"
                    {...props}
                  >
                    {children}
                  </code>
                ) : (
                  <code
                    className={`${className} block p-3 rounded-md bg-muted text-sm overflow-x-auto`}
                    {...props}
                  >
                    {children}
                  </code>
                );
            },
            // Custom styling for tables
            table: ({ children, ...props }) => (
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse border border-border" {...props}>
                  {children}
                </table>
              </div>
            ),
            th: ({ children, ...props }) => (
              <th className="border border-border px-4 py-2 bg-muted font-medium text-left" {...props}>
                {children}
              </th>
            ),
            td: ({ children, ...props }) => (
              <td className="border border-border px-4 py-2" {...props}>
                {children}
              </td>
            ),
            // Custom styling for blockquotes
            blockquote: ({ children, ...props }) => (
              <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground" {...props}>
                {children}
              </blockquote>
            ),
            // Custom styling for links
            a: ({ children, href, ...props }) => (
              <a
                href={href}
                className="text-primary hover:underline"
                target="_blank"
                rel="noopener noreferrer"
                {...props}
              >
                {children}
              </a>
            ),
          }}
          >
            {markdownContent}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}